// Minimal RFC 5545 (iCalendar) parse/serialize for the portal's CalDAV client.
// Handles VEVENT + VTODO with the properties the calendar service emits; anything
// unrecognised is preserved on the parsed object as `unknownProps` and round-tripped.

const CRLF = "\r\n";

// ---------- parsing ----------

const unfold = (text) =>
  text.replace(/\r?\n[ \t]/g, "").split(/\r?\n/).filter(Boolean);

const unescapeText = (value) =>
  value
    .replace(/\\n/gi, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/\\\\/g, "\\");

// Splits a content line into { name, params, value }. Parameter values may be
// quoted and contain ':' or ';', so scan rather than split.
const parseLine = (line) => {
  let inQuotes = false;
  let splitAt = -1;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') inQuotes = !inQuotes;
    else if (ch === ":" && !inQuotes) {
      splitAt = i;
      break;
    }
  }
  if (splitAt === -1) return null;

  const head = line.slice(0, splitAt);
  const value = line.slice(splitAt + 1);
  const [name, ...paramParts] = head.split(";");
  const params = {};
  for (const part of paramParts) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    params[part.slice(0, eq).toUpperCase()] = part.slice(eq + 1).replace(/^"|"$/g, "");
  }
  return { name: name.toUpperCase(), params, value };
};

// DATE => local midnight; DATE-TIME ...Z => UTC; TZID/floating => local wall time.
// The calendar service normalises to UTC, so the TZID fallback is best-effort.
const parseIcsDate = (value, params = {}) => {
  if (!value) return null;
  const isDateOnly = params.VALUE === "DATE" || /^\d{8}$/.test(value);
  if (isDateOnly) {
    const y = +value.slice(0, 4);
    const m = +value.slice(4, 6) - 1;
    const d = +value.slice(6, 8);
    return { date: new Date(y, m, d), allDay: true };
  }
  const match = value.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z?)$/);
  if (!match) return null;
  const [, y, mo, d, h, mi, s, z] = match;
  const date = z
    ? new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s))
    : new Date(+y, +mo - 1, +d, +h, +mi, +s);
  return { date, allDay: false };
};

const componentKind = { VEVENT: "event", VTODO: "task" };

// The calendar service redacts the real description on projected items and
// injects this placeholder in the CalDAV/ICS representation. It's noise in the
// portal (which is "Ontik"), so treat it as an empty description everywhere.
const SERVICE_PLACEHOLDER_DESCRIPTION = "Open Ontik to view details and take notes.";

// Work Ontik scheduled on the merchant's behalf (a promise to pay, a payment plan
// instalment) carries its identity on the feed as X- properties. Nothing else in the
// CalDAV representation can say "this row is a £60 promise-to-pay send": SUMMARY is
// model-authored free text and DESCRIPTION is redacted to the placeholder above, so
// without these the grid can only show a generic chip. The raw lines stay in
// `unknownProps` too, so editing an item round-trips them untouched.
const PROJECTION_PROPS = {
  "X-ONTIK-ACTION-TYPE": "actionType",
  "X-ONTIK-REFERENCE": "reference",
  "X-ONTIK-AMOUNT": "amount",
  "X-ONTIK-CURRENCY": "currency",
  "X-ONTIK-INSTALMENT": "instalment",
  "X-ONTIK-INSTALMENT-TOTAL": "instalmentTotal",
  // "promise" | "schedule" — a PromiseToPay-shaped projection is a customer commitment by default;
  // this distinguishes a merchant-scheduled send so the grid/detail surfaces can give it its own
  // identity (see areas/calendar/common.jsx's projectionIdentity).
  "X-ONTIK-SCHEDULE-KIND": "scheduleKind",
  // Fields carried on recommended-task items for the "Today's tasks" page.
  "X-ONTIK-TASK-TYPE": "taskType",
  "X-ONTIK-USER-ID": "userId",
  "X-ONTIK-CUSTOMER-ID": "customerId",
  "X-ONTIK-CUSTOMER-NAME": "customerName",
  "X-ONTIK-CUSTOMER-REF": "customerRef",
};

const parseComponent = (lines, type) => {
  const item = {
    kind: componentKind[type],
    uid: null,
    summary: "",
    description: "",
    location: "",
    url: null,
    start: null,
    end: null,
    due: null,
    allDay: false,
    status: null,
    categories: [],
    rrule: null,
    exdates: [],
    sequence: 0,
    priority: 0,
    organizer: null,
    attendees: [],
    projection: null,
    customerId: null,
    unknownProps: [],
  };

  let depth = 0;
  for (const raw of lines) {
    const parsed = parseLine(raw);
    if (!parsed) continue;
    const { name, params, value } = parsed;

    // Skip nested components (VALARM etc.) but keep them for round-tripping.
    if (name === "BEGIN") {
      depth++;
      item.unknownProps.push(raw);
      continue;
    }
    if (name === "END") {
      depth--;
      item.unknownProps.push(raw);
      continue;
    }
    if (depth > 0) {
      item.unknownProps.push(raw);
      continue;
    }

    switch (name) {
      case "UID":
        item.uid = value;
        break;
      case "SUMMARY":
        item.summary = unescapeText(value);
        break;
      case "DESCRIPTION":
        item.description = unescapeText(value);
        break;
      case "LOCATION":
        item.location = unescapeText(value);
        break;
      case "URL":
        item.url = value;
        break;
      case "DTSTART": {
        const dt = parseIcsDate(value, params);
        if (dt) {
          item.start = dt.date;
          item.allDay = dt.allDay;
        }
        break;
      }
      case "DTEND": {
        const dt = parseIcsDate(value, params);
        if (dt) item.end = dt.date;
        break;
      }
      case "DUE": {
        const dt = parseIcsDate(value, params);
        if (dt) {
          item.due = dt.date;
          if (dt.allDay) item.allDay = true;
        }
        break;
      }
      case "STATUS":
        item.status = value.toUpperCase();
        break;
      case "CATEGORIES":
        item.categories.push(
          ...value.split(",").map((c) => unescapeText(c.trim())).filter(Boolean),
        );
        break;
      case "RRULE":
        item.rrule = value;
        break;
      case "EXDATE": {
        for (const part of value.split(",")) {
          const dt = parseIcsDate(part.trim(), params);
          if (dt) item.exdates.push(dt.date);
        }
        break;
      }
      case "SEQUENCE":
        item.sequence = parseInt(value, 10) || 0;
        break;
      case "PRIORITY":
        item.priority = parseInt(value, 10) || 0;
        break;
      case "ORGANIZER":
        item.organizer = {
          email: value.replace(/^mailto:/i, ""),
          name: params.CN || null,
        };
        break;
      case "ATTENDEE":
        item.attendees.push({
          email: value.replace(/^mailto:/i, ""),
          name: params.CN || null,
          role: params.ROLE || null,
          partstat: params.PARTSTAT || null,
        });
        break;
      case "RELATED-TO": {
        // Links the item to its source action / customer / invoice. Pull the customer id out so the
        // chip can resolve and show the account code; the raw line still round-trips via unknownProps.
        const match = /^customer\/(.+)$/i.exec(value.trim());
        if (match) item.customerId = match[1];
        item.unknownProps.push(raw);
        break;
      }
      case "DTSTAMP":
      case "CREATED":
      case "LAST-MODIFIED":
      case "PRODID":
      case "VERSION":
        break;
      default: {
        const field = PROJECTION_PROPS[name];
        if (field) {
          item.projection = { ...item.projection, [field]: unescapeText(value) };
        }
        item.unknownProps.push(raw);
      }
    }
  }

  // The feed redacts every description down to this sentinel (privacy: the .ics is externally
  // subscribable). It isn't real data, so drop it — the in-app modal/edit form read the real
  // description from the authenticated REST detail endpoint instead.
  if (item.description.trim() === SERVICE_PLACEHOLDER_DESCRIPTION) {
    item.description = "";
  }

  if (item.projection && "amount" in item.projection) {
    const amount = Number.parseFloat(item.projection.amount);
    item.projection.amount = Number.isFinite(amount) ? amount : null;
  }

  for (const key of ["instalment", "instalmentTotal"]) {
    if (item.projection && key in item.projection) {
      const n = Number.parseInt(item.projection[key], 10);
      item.projection[key] = Number.isFinite(n) ? n : null;
    }
  }

  return item;
};

/** Parses an iCalendar document into an array of event/task objects. */
export const parseIcs = (text) => {
  const lines = unfold(text || "");
  const items = [];
  let current = null;
  let currentType = null;
  let nested = 0;

  for (const line of lines) {
    const upper = line.toUpperCase();
    if (upper.startsWith("BEGIN:")) {
      const type = upper.slice(6);
      if (current) {
        nested++;
        current.push(line);
      } else if (componentKind[type]) {
        current = [];
        currentType = type;
      }
      continue;
    }
    if (upper.startsWith("END:")) {
      const type = upper.slice(4);
      if (current && type === currentType && nested === 0) {
        items.push(parseComponent(current, currentType));
        current = null;
        currentType = null;
      } else if (current) {
        nested--;
        current.push(line);
      }
      continue;
    }
    if (current) current.push(line);
  }

  return items.filter((i) => i.uid);
};

// ---------- serializing ----------

const escapeText = (value) =>
  String(value ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");

const pad = (n) => String(n).padStart(2, "0");

const toIcsDateTimeUtc = (date) =>
  `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T` +
  `${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;

const toIcsDate = (date) =>
  `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;

// RFC 5545 §3.1: content lines should not exceed 75 octets; fold with CRLF + space.
const fold = (line) => {
  if (line.length <= 74) return line;
  const parts = [];
  let rest = line;
  while (rest.length > 74) {
    parts.push(rest.slice(0, 74));
    rest = " " + rest.slice(74);
  }
  parts.push(rest);
  return parts.join(CRLF);
};

/**
 * Builds a single-component VCALENDAR document from an event/task object as
 * produced by parseIcs (or assembled by the UI).
 */
export const buildIcs = (item) => {
  const type = item.kind === "task" ? "VTODO" : "VEVENT";
  const now = toIcsDateTimeUtc(new Date());
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Ontik//Portal Calendar//EN",
    `BEGIN:${type}`,
    `UID:${item.uid}`,
    `DTSTAMP:${now}`,
    `SUMMARY:${escapeText(item.summary)}`,
  ];

  if (item.location) lines.push(`LOCATION:${escapeText(item.location)}`);
  if (item.url) lines.push(`URL:${item.url}`);

  if (item.start) {
    lines.push(
      item.allDay
        ? `DTSTART;VALUE=DATE:${toIcsDate(item.start)}`
        : `DTSTART:${toIcsDateTimeUtc(item.start)}`,
    );
  }
  if (item.end) {
    lines.push(
      item.allDay
        ? `DTEND;VALUE=DATE:${toIcsDate(item.end)}`
        : `DTEND:${toIcsDateTimeUtc(item.end)}`,
    );
  }
  if (item.due) {
    lines.push(
      item.allDay
        ? `DUE;VALUE=DATE:${toIcsDate(item.due)}`
        : `DUE:${toIcsDateTimeUtc(item.due)}`,
    );
  }

  if (item.status) lines.push(`STATUS:${item.status}`);
  if (item.sequence) lines.push(`SEQUENCE:${item.sequence}`);
  if (item.priority) lines.push(`PRIORITY:${item.priority}`);
  if (item.categories?.length) {
    lines.push(`CATEGORIES:${item.categories.map(escapeText).join(",")}`);
  }
  if (item.rrule) lines.push(`RRULE:${item.rrule}`);
  for (const exdate of item.exdates ?? []) {
    lines.push(`EXDATE:${toIcsDateTimeUtc(exdate)}`);
  }

  if (item.organizer?.email) {
    const cn = item.organizer.name ? `;CN=${item.organizer.name}` : "";
    lines.push(`ORGANIZER${cn}:mailto:${item.organizer.email}`);
  }
  for (const attendee of item.attendees ?? []) {
    if (!attendee.email) continue;
    const params = [
      attendee.name && `CN=${attendee.name}`,
      attendee.role && `ROLE=${attendee.role}`,
      attendee.partstat && `PARTSTAT=${attendee.partstat}`,
    ]
      .filter(Boolean)
      .map((p) => `;${p}`)
      .join("");
    lines.push(`ATTENDEE${params}:mailto:${attendee.email}`);
  }

  // Round-trip properties and nested components (VALARM etc.) we don't model,
  // so editing in the portal never silently drops data written by other clients.
  for (const raw of item.unknownProps ?? []) {
    lines.push(raw);
  }

  // DESCRIPTION is written LAST — always, even when empty — after the round-tripped nested components.
  // A reminder VALARM carries its own DESCRIPTION (the task title), and the calendar service's ICS
  // reader is not component-aware: it takes the LAST DESCRIPTION line in the payload. Emitting the
  // item's own description last (an empty line when cleared) guarantees it wins over the alarm's, so a
  // blank description clears rather than being clobbered by the title. buildIcs only runs from the edit
  // form, where item.description is authoritative — there's no drag/reschedule path that would re-save
  // a grid item's redacted-blank description.
  lines.push(`DESCRIPTION:${escapeText(item.description ?? "")}`);

  lines.push(`END:${type}`, "END:VCALENDAR");
  return lines.map(fold).join(CRLF) + CRLF;
};

// ---------- recurrence expansion ----------

const WEEKDAYS = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };

const parseRrule = (rrule) => {
  const rule = {};
  for (const part of (rrule || "").split(";")) {
    const [key, value] = part.split("=");
    if (key && value) rule[key.toUpperCase()] = value;
  }
  return rule;
};

/**
 * Expands a recurring item into concrete occurrences within [rangeStart, rangeEnd).
 * Supports FREQ=DAILY/WEEKLY/MONTHLY/YEARLY with INTERVAL, COUNT, UNTIL and
 * BYDAY (weekly). Unsupported rules fall back to the base occurrence only.
 */
export const expandOccurrences = (item, rangeStart, rangeEnd) => {
  if (!item.start) return [];
  const durationMs = item.end ? item.end.getTime() - item.start.getTime() : 0;

  const occurrence = (start) => ({
    ...item,
    start,
    end: item.end ? new Date(start.getTime() + durationMs) : null,
    occurrenceKey: `${item.uid}:${start.getTime()}`,
    isOccurrence: start.getTime() !== item.start.getTime(),
  });

  const inRange = (d) => d < rangeEnd && (durationMs ? new Date(d.getTime() + durationMs) : d) >= rangeStart;
  const excluded = (d) => (item.exdates ?? []).some((x) => x.getTime() === d.getTime());

  if (!item.rrule) {
    return inRange(item.start) ? [occurrence(item.start)] : [];
  }

  const rule = parseRrule(item.rrule);
  const freq = rule.FREQ;
  if (!["DAILY", "WEEKLY", "MONTHLY", "YEARLY"].includes(freq)) {
    return inRange(item.start) ? [occurrence(item.start)] : [];
  }

  const interval = Math.max(1, parseInt(rule.INTERVAL, 10) || 1);
  const count = rule.COUNT ? parseInt(rule.COUNT, 10) : null;
  const until = rule.UNTIL ? parseIcsDate(rule.UNTIL)?.date : null;
  const byDay =
    freq === "WEEKLY" && rule.BYDAY
      ? rule.BYDAY.split(",").map((d) => WEEKDAYS[d]).filter((d) => d !== undefined)
      : null;

  const results = [];
  let produced = 0;
  let cursor = new Date(item.start);
  const hardStop = 1000;

  for (let i = 0; i < hardStop; i++) {
    const candidates = [];
    if (byDay) {
      // Expand each requested weekday within the current week.
      const weekStart = new Date(cursor);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      for (const dow of byDay) {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + dow);
        d.setHours(cursor.getHours(), cursor.getMinutes(), cursor.getSeconds(), 0);
        if (d >= item.start) candidates.push(d);
      }
      candidates.sort((a, b) => a - b);
    } else {
      candidates.push(new Date(cursor));
    }

    for (const candidate of candidates) {
      if (until && candidate > until) return results;
      if (count !== null && produced >= count) return results;
      produced++;
      if (candidate >= rangeEnd) return results;
      if (inRange(candidate) && !excluded(candidate)) results.push(occurrence(candidate));
    }

    if (count !== null && produced >= count) return results;

    if (freq === "DAILY") cursor.setDate(cursor.getDate() + interval);
    else if (freq === "WEEKLY") cursor.setDate(cursor.getDate() + 7 * interval);
    else if (freq === "MONTHLY") cursor.setMonth(cursor.getMonth() + interval);
    else cursor.setFullYear(cursor.getFullYear() + interval);

    if (until && cursor > until && !byDay) return results;
    if (cursor >= rangeEnd && !byDay) return results;
  }

  return results;
};
